import { asExpression, normalizeAggregateSpec, evaluateAggregate, evaluateNativeExpression } from "./nativeExpression.mjs";
import { admitNativeExecution, nativeVisibility, prepareNativeVisibility, VISIBILITY_SUPPRESSION_NOTICE } from "./nativeVisibility.mjs";
import { nativeTypedExecution, prepareNativeTypedExecution } from "./nativeTypedExecution.mjs";

const PAGE_SIZES = {
  Letter: { width: 612, height: 792 },
  Legal: { width: 612, height: 1008 },
  A4: { width: 595.28, height: 841.89 },
};

const DEFAULT_MARGINS = { top: 36, right: 36, bottom: 36, left: 36 };
export const NATIVE_CONTAINER_MAX_DEPTH = 6;
const DEFAULT_TABLE_PADDING = { top: 4, right: 3, bottom: 4, left: 3 };
const DEFAULT_TABLE_STYLE = {
  fontSize: 10,
  headerFontSize: 10,
  footerFontSize: 10,
  lineHeight: 1.25,
  borderColor: "#808080",
  rowBorderColor: "#d8e1ec",
  headerBackgroundColor: "#f0f5ff",
  footerBackgroundColor: "#f8fbff",
  color: "#000000",
};

function presentationStyle(payload, target, context, identity, base, broadIdentity) {
  const execution = nativeVisibility(payload);
  return execution?.formatting ? execution.style(target, context, identity, base, broadIdentity) : { style: base };
}

export function isNativeReportsV31Payload(payload) {
  const layout = asRecord(payload?.layout);
  return payload?.schemaVersion === "reports-v3.1" && Array.isArray(layout.bands);
}

export function nativeWorkbookSupport(payload) {
  try {
    validateNativeContentProfile(payload);
    return { supported: true };
  } catch (error) {
    return { supported: false, reason: error.message };
  }
}

export function resolveNativeContentPlan(payload, { maxRows = 10000, maxCells = 500000, maxTables = 31, visibilityExecution, typedExecution } = {}) {
  payload = prepareNativeTypedExecution(payload, typedExecution);
  payload = prepareNativeVisibility(payload, visibilityExecution);
  validateNativeContentProfile(payload);
  const byRole = groupBandsByRole(prepareNativeBands(payload));
  const blocks = [];
  const sections = [];
  const notices = new Set(["Native export: spreadsheet-adapted layout, precomputed values, no Excel formulas. Editing cells does not recalculate report totals.", "Hidden Data columns are excluded. Include-hidden opt-in is deferred; no public option is accepted.", "PDF page breaks and group spacers are compacted; group outlines are deferred."]);
  let rowCount = 0;
  let cellCount = 0;
  let tableCount = 0;
  function visit(elements, band, row, instance, parentVisible = true) {
    const children = [];
    for (const element of elements) {
      const visibility = nativeVisibility(payload);
      const visible = parentVisible && (!visibility || visibility.visible(element, visibility.context(row), `${instance}/${element.id}`));
      const presentation = element.type === "table" ? {} : presentationStyle(payload, element, visibility?.context(row), `${instance}/${element.id}`, element.style);
      if (element.type === "container") {
        const descendants = visit(element.elements || [], band, row, instance, visible);
        if (visible) children.push({ kind: "section", id: `${instance}/${element.id}`, role: band.role, ...presentation, mode: element.layout?.mode || "absolute", children: descendants });
      } else if (element.type === "image") {
        if (visible) children.push({ kind: "image", id: `${instance}/${element.id}`, role: band.role, asset: element.asset, width: element.width, height: element.height, fit: element.fit, ...presentation });
      } else if (element.type === "table") {
        const info = tableRowsInfo(payload, element.dataset || band.dataset, row);
        if (!["ok", "zeroRecords"].includes(info.reason)) throw nativeContentError(`Table "${element.id}": ${nativeTableEmptyText(element.dataset || band.dataset, info)}`);
        validateNativeRecordRows(info.rows);
        rowCount += info.rows.length;
        tableCount += 1;
        const columns = element.columns.filter((column) => column.visible !== false && column.hidden !== true);
        cellCount += info.rows.length * columns.length * (visibility ? 1 : 2);
        if (rowCount > maxRows || tableCount > maxTables || cellCount > maxCells) throw nativeContentError("Native workbook row, table or cell limit exceeded. Reduce records or tables.", 413, "NATIVE_WORKBOOK_LIMIT");
        const plan = buildNativeTablePlan(payload, element, band.dataset, row, instance);
        const cell = (value) => ({
          columnId: value.columnId,
          colSpan: value.colSpan,
          rawValue: Object.prototype.hasOwnProperty.call(value, "rawValue") ? value.rawValue : value.value,
          value: value.value,
          format: value.format,
          horizontalAlign: value.style?.horizontalAlign,
          style: value.style,
          ...(value.conditionalStyle ? { conditionalStyle: true, matchedRuleIds: value.matchedRuleIds } : {}),
          ...(value.reportVisible === false ? { reportVisible: false } : {}),
        });
        const authoredColumns = new Map(element.columns.map((column) => [column.id, column]));
        const footerCells = (footer) => {
          const result = new Map();
          for (const [index, candidate] of (footer?.cells || []).entries()) {
            const projected = projectTableCellToVisibleColumns(candidate, index, element.columns, plan.columns);
            if (projected && !result.has(projected.columnId)) result.set(projected.columnId, candidate);
          }
          return result;
        };
        const groupFooterCells = footerCells(element.groups?.[0]?.footer);
        const tableFooterCells = footerCells(element.footer);
        const presentationCell = (value, entry) => {
          const output = cell(value);
          if (visibility?.formatting) return output;
          if (entry.kind === "detailRow") {
            output.style = { ...value.style, borderColor: authoredColumns.get(value.columnId)?.style?.borderColor ?? element.row?.style?.rowBorderColor ?? element.row?.style?.borderColor ?? element.style?.rowBorderColor ?? element.style?.borderColor ?? plan.style.rowBorderColor };
          }
          if (!["groupFooter", "tableFooter"].includes(entry.kind)) return output;
          const footer = entry.kind === "groupFooter" ? element.groups?.[0]?.footer : element.footer;
          const authored = (entry.kind === "groupFooter" ? groupFooterCells : tableFooterCells).get(value.columnId);
          output.style = {
            ...value.style,
            fontSize: authored?.style?.fontSize ?? footer?.style?.fontSize ?? (entry.kind === "groupFooter" ? element.groupFooter?.style?.fontSize : undefined) ?? element.style?.footerFontSize ?? plan.style.footerFontSize ?? value.style?.fontSize,
            backgroundColor: value.style?.backgroundColor ?? element.style?.footerBackgroundColor,
          };
          return output;
        };
        const flowRows = plan.flowRows.filter((entry) => entry.kind !== "groupSpacer").map((entry) => ({
          kind: entry.kind, sourceIndex: entry.rowIndex, groupId: entry.groupId, groupKey: entry.groupKey,
          cells: entry.cells.map((value) => presentationCell(value, entry)),
        }));
        const block = {
          kind: "table", id: `${instance}/${element.id}`, tableId: element.id, dataset: plan.dataset,
          ...(visibility ? { reportVisible: visible && plan.reportVisible, analysisColumns: plan.analysisColumns, analysisRows: plan.analysisRows } : {}),
          style: plan.style, headerCells: plan.header.cells.map((value) => cell(visibility?.formatting ? value : { ...value, style: { ...value.style, fontSize: element.header?.style?.fontSize ?? element.header?.style?.headerFontSize ?? element.style?.headerFontSize ?? plan.style.headerFontSize ?? value.style?.fontSize } })), repeatHeader: plan.repeatHeader,
          ...(visibility ? { headerVisible: plan.header.reportVisible !== false } : {}),
          columns: plan.columns.map((column) => ({ id: column.id, label: column.label, binding: column.binding, format: column.format, width: column.width })),
          rows: visibility ? plan.analysisRows : flowRows.filter((entry) => entry.kind === "detailRow"),
          flowRows,
          summaries: flowRows.filter((entry) => ["groupFooter", "tableFooter"].includes(entry.kind)),
          emptyText: plan.flowRows.find((entry) => entry.kind === "empty")?.cells[0]?.value,
        };
        blocks.push(block);
        children.push(block);
      } else {
        if (!visible) continue;
        if (element.binding === "pageNumber" || /\{\{\s*pageNumber\s*\}\}/.test(element.text || "")) {
          children.push({ kind: "pageNumber", id: `${instance}/${element.id}`, role: band.role, text: element.text, ...presentation });
          continue;
        }
        const block = { kind: "value", id: `${instance}/${element.id}`, role: band.role, elementType: element.type, ...presentation, format: element.type === "field" || element.expr?.typed ? element.format : undefined, ...resolveNativeValueContent(payload, element, row, band.dataset) };
        blocks.push(block);
        children.push(block);
      }
    }
    return children;
  }
  for (const role of ["pageHeader", "reportHeader", "detail", "reportFooter", "pageFooter"]) {
    for (const band of byRole[role]) {
      const rows = role === "detail" ? detailRows(payload, band.dataset) : [];
      if (nativeVisibility(payload) && role === "detail" && band.dataset && !rows.length) continue;
      validateNativeRecordRows(rows);
      if (rows.length > maxRows) throw nativeContentError("Native detail-band row limit exceeded.", 413, "NATIVE_WORKBOOK_LIMIT");
      for (const [index, row] of (rows.length ? rows : [undefined]).entries()) {
        const visible = bandVisible(payload, band, row, index);
        sections.push({ kind: "section", id: `${band.id}/${index}`, role: band.role, mode: "vertical", children: visit(band.elements, band, row, `${band.id}/${index}`, visible) });
        if (blocks.length > maxCells) throw nativeContentError("Native content block limit exceeded.", 413, "NATIVE_WORKBOOK_LIMIT");
      }
    }
  }
  for (const notice of nativeVisibility(payload)?.notices || []) notices.add(notice);
  return { schemaVersion: "reports-v3.1", definitionId: payload.definitionId, blocks, sections, page: normalizePage(payload.layout.page), notices: [...notices], rowCount,
    ...(nativeVisibility(payload)?.formatting ? { conditionalFormatting: true, styleCount: nativeVisibility(payload).styleCount } : {}) };
}

function nativeContentError(message, status = 400, code = "UNSUPPORTED_NATIVE_WORKBOOK") {
  return Object.assign(new Error(message), { status, code });
}

function validateNativeRecordRows(rows) {
  if (rows.some((row) => row === null || typeof row !== "object" || Array.isArray(row))) throw nativeContentError("Native repeating datasets require record objects, not null, arrays or scalar rows.");
}

function validateNativeContentProfile(payload, requireTable = true) {
  admitNativeExecution(payload);
  const fail = (message) => { throw nativeContentError(message); };
  if (!isNativeReportsV31Payload(payload) || !payload.layout.bands.length) fail("Native Excel requires reports-v3.1 layout.bands.");
  if (!payload.datasets || Array.isArray(payload.datasets) || typeof payload.datasets !== "object") fail("Native Excel requires a dataset dictionary.");
  const identities = new Set();
  const record = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
  let tables = 0;
  const identity = (value) => {
    if (typeof value !== "string" || !value.trim() || identities.has(value)) fail("Native band/element identities must be nonempty and unique.");
    identities.add(value);
  };
  const binding = (value) => {
    if (typeof value !== "string" || !value.trim() || value.split(".").some((part) => ["__proto__", "prototype", "constructor"].includes(part))) fail("Unsupported native field binding.");
  };
  let expressionNodes = 0;
  function expression(value, depth = 0) {
    if (depth === 0 && value?.typed !== undefined && nativeTypedExecution(payload)) return;
    if (depth === 0) expressionNodes = 0;
    if (++expressionNodes > 256) fail("Native expression node limit exceeded.");
    if (!value || typeof value !== "object" || Array.isArray(value) || depth > 16) fail("Invalid or excessively nested native expression.");
    if (Object.prototype.hasOwnProperty.call(value, "const")) {
      if (typeof value.const !== "number" || !Number.isFinite(value.const)) fail("Native constants must be finite numbers.");
    } else if (value.field) binding(value.field);
    else if (value.aggregate) aggregate(value.aggregate, depth + 1);
    else {
      if (!["add", "sub", "mul", "div"].includes(value.op)) fail("Unsupported native expression operation.");
      const args = Array.isArray(value.args) && value.args.length ? value.args : [value.left, value.right].filter(Boolean);
      if (args.length < (nativeVisibility(payload) && value.op === "sub" ? 1 : 2)) fail("Native arithmetic expressions require explicit operands.");
      args.forEach((arg) => expression(arg, depth + 1));
    }
  }
  function aggregate(value, depth = 0) {
    const spec = typeof value === "string" ? { fn: value } : value;
    if (!spec || !["sum", "count", "min", "max", "avg"].includes(spec.fn || spec.function)) fail("Unsupported native aggregate.");
    if (spec.scope && !["group", "report"].includes(spec.scope)) fail("Unsupported native aggregate scope.");
    if (spec.field) binding(spec.field);
    if (spec.expr || spec.expression) expression(spec.expr || spec.expression, depth + 1);
  }
  function elements(values, band, depth = 0) {
    if (!Array.isArray(values) || depth > NATIVE_CONTAINER_MAX_DEPTH) fail("Invalid native elements or excessive container depth.");
    for (const element of values) {
      if (!element || typeof element !== "object" || !["text", "field", "image", "container", "table"].includes(element.type)) fail("Unsupported native element. Export supports text, fields, containers and flow tables; images produce a notice.");
      identity(element.id);
      if (["x", "y", "width", "height"].some((key) => element[key] !== undefined && (typeof element[key] !== "number" || !Number.isFinite(element[key]) || element[key] < 0)) || element.width === 0) fail(`Element "${element.id}" requires finite nonnegative geometry and positive width when supplied.`);
      if (element.format && !["money", "number", "date"].includes(element.format)) fail(`Element "${element.id}" has an unsupported format.`);
      if (element.type === "field") binding(element.binding);
      if (element.type === "image" && element.asset !== undefined && (typeof element.asset !== "string" || !element.asset.trim())) fail("Native image asset reference must be a nonempty string when supplied.");
      if (element.type === "text" && typeof element.text !== "string" && !(element.expr?.typed !== undefined && nativeTypedExecution(payload))) fail("Native text elements require static text or an admitted typed expression.");
      if (element.type === "container") {
        if (element.layout?.mode && !["absolute", "vertical", "columns"].includes(element.layout.mode)) fail("Unsupported native container layout.");
        elements(element.elements, band, depth + 1);
      }
      if (element.type !== "table") continue;
      tables += 1;
      if (depth || band.sizing !== "flow") fail(`Table "${element.id}" requires a top-level flow band. Move it to a supported flow band.`);
      if (Array.isArray(element.columns) && element.columns.some((column) => !record(column))) fail("Native columns must be objects.");
      if (!Array.isArray(element.columns) || !element.columns.length || !element.columns.some((column) => column.visible !== false && column.hidden !== true)) fail(`Table "${element.id}" needs at least one visible column.`);
      if (element.columns.length > 128) fail("Native foundation export supports at most 128 authored columns per table.");
      if (element.groups !== undefined && !Array.isArray(element.groups)) fail("Native groups must be an array.");
      if (element.sort !== undefined && !Array.isArray(element.sort)) fail("Native sort must be an array.");
      if ((element.groups?.length || 0) > 1) fail("Multiple native grouping levels are not supported yet.");
      if (element.dataset) binding(element.dataset);
      const ids = new Set();
      for (const column of element.columns) {
        if (typeof column.id !== "string" || !column.id || ids.has(column.id) || ["__proto__", "constructor", "prototype"].includes(column.id)) fail("Native column identities must be safe, nonempty and unique within a table.");
        ids.add(column.id);
        if (["visible", "hidden"].some((key) => column[key] !== undefined && typeof column[key] !== "boolean")) fail("Native column visibility must be boolean.");
        if (column.format && !["money", "number", "date"].includes(column.format)) fail(`Column "${column.id}" has an unsupported format.`);
        if (column.expr || column.calculation) expression(column.expr || column.calculation);
        else binding(column.binding || column.key);
      }
      for (const sort of element.sort || []) {
        if (!record(sort)) fail("Native sort rules must be objects.");
        binding(sort.field);
        if (sort.direction && !["asc", "desc", "ascending", "descending"].includes(sort.direction)) fail("Unsupported native sort direction.");
        if (sort.type && !["auto", "date", "number", "string"].includes(sort.type)) fail("Unsupported native sort type.");
        if (sort.nulls && !["first", "last"].includes(sort.nulls)) fail("Unsupported native null ordering.");
      }
      for (const group of element.groups || []) {
        if (!record(group)) fail("Native groups must be objects.");
        if (group.sort && !["asc", "desc", "ascending", "descending"].includes(group.sort)) fail("Unsupported native group direction.");
        if (group.expr || group.expression || group.key?.expr) expression(group.expr || group.expression || group.key.expr);
        else binding(group.field || group.key?.field);
      }
      for (const footer of [element.footer, ...(element.groups || []).map((group) => group.footer)]) {
        if (footer !== undefined && (!record(footer) || (footer.cells !== undefined && !Array.isArray(footer.cells)))) fail("Native footer cells must be an array.");
        for (const cell of footer?.cells || []) {
          if (!record(cell) || !ids.has(cell.columnId)) fail("Native footer cells require a known column identity.");
          if (cell.colSpan !== undefined && (!Number.isSafeInteger(cell.colSpan) || cell.colSpan < 1 || cell.colSpan > ids.size)) fail("Native footer spans must be positive integers within the table.");
          if (cell.format && !["money", "number", "date"].includes(cell.format)) fail("Unsupported native summary format.");
          if (record(cell.aggregate) && cell.aggregate.format && !["money", "number", "date"].includes(cell.aggregate.format)) fail("Unsupported native aggregate format.");
          if (cell.aggregate) aggregate(cell.aggregate);
        }
      }
      for (const column of element.columns) if (column.aggregate) aggregate(column.aggregate);
    }
    }
  for (const band of payload.layout.bands) {
    if (!band || !["reportHeader", "pageHeader", "detail", "reportFooter", "pageFooter"].includes(band.role)) fail("Unsupported native band role.");
    if (typeof band.id !== "string" || !band.id || !Number.isFinite(band.height) || band.height < 0) fail("Native bands require an identity and finite nonnegative height.");
    identity(`band:${band.id}`);
    if (band.dataset) binding(band.dataset);
    elements(band.elements, band);
    if (band.sizing === "flow") {
      const issue = validateFlowBandShape(band);
      if (issue) fail(issue);
    }
  }
  if (requireTable && !tables) fail("Native Excel foundation requires a supported repeating table. Document-only workbooks are deferred.");
}

function resolveNativeValueContent(payload, element, row, datasetName) {
  const typed = element.expr?.typed !== undefined;
  const rawValue = typed ? nativeTypedExecution(payload).text(element, row) : element.type === "field"
    ? resolveNativeBinding(payload, element.binding, row, datasetName)
    : resolveTemplate(payload, element.text || "", row, datasetName);
  return { rawValue, value: element.type === "field" || typed ? formatNativeValue(rawValue, element.format) : rawValue };
}

export function resolveNativeLayoutPlan(payload, { visibilityExecution, typedExecution } = {}) {
  payload = prepareNativeTypedExecution(payload, typedExecution);
  payload = prepareNativeVisibility(payload, visibilityExecution);
  if (nativeVisibility(payload) || nativeTypedExecution(payload)) validateNativeContentProfile(payload, false);
  const nativeLayout = asRecord(payload.layout);
  const pageSetup = normalizePage(nativeLayout.page);
  const bands = prepareNativeBands(payload);
  const byRole = groupBandsByRole(bands);
  for (const role of ["pageHeader", "pageFooter", "reportHeader", "reportFooter"]) byRole[role] = byRole[role].filter((band) => bandVisible(payload, band));
  const pageHeaderHeight = sumBandHeights(byRole.pageHeader);
  const pageFooterHeight = sumBandHeights(byRole.pageFooter);
  const contentTop = pageSetup.printableArea.y + pageHeaderHeight;
  const contentBottom = pageSetup.printableArea.y + pageSetup.printableArea.height - pageFooterHeight;
  const overflow = [];
  const pages = [];

  let currentPage = createPage(pageSetup, pages.length + 1);
  pages.push(currentPage);
  addFixedBands(payload, currentPage, byRole.pageHeader, pageSetup.printableArea.y, overflow);
  addFixedBands(payload, currentPage, byRole.pageFooter, contentBottom, overflow);
  let cursorY = contentTop;

  for (const band of byRole.reportHeader) {
    if (band.pageBreakBefore && currentPage.bands.length > byRole.pageHeader.length + byRole.pageFooter.length) {
      ({ page: currentPage, cursorY } = addPage(pageSetup, pages, byRole, payload, overflow, contentTop, contentBottom));
    }
    if (cursorY + band.height > contentBottom) {
      ({ page: currentPage, cursorY } = addPage(pageSetup, pages, byRole, payload, overflow, contentTop, contentBottom));
    }
    if (isFlowBand(band)) {
      ({ page: currentPage, cursorY } = addFlowBand(payload, currentPage, pages, byRole, pageSetup, band, cursorY, undefined, overflow, contentTop, contentBottom));
    } else {
      addResolvedBand(payload, currentPage, band, cursorY, undefined, overflow);
      cursorY += band.height;
    }
    if (band.pageBreakAfter) {
      ({ page: currentPage, cursorY } = addPage(pageSetup, pages, byRole, payload, overflow, contentTop, contentBottom));
    }
  }

  for (const band of byRole.detail) {
    const rows = detailRows(payload, band.dataset);
    if (nativeVisibility(payload) && band.dataset && !rows.length) continue;
    const effectiveRows = rows.length ? rows : [{}];
    for (const [rowIndex, row] of effectiveRows.entries()) {
      if (!bandVisible(payload, band, rows.length ? row : undefined, rowIndex)) continue;
      if (band.pageBreakBefore || cursorY + band.height > contentBottom) {
        ({ page: currentPage, cursorY } = addPage(pageSetup, pages, byRole, payload, overflow, contentTop, contentBottom));
      }
      if (isFlowBand(band)) {
        ({ page: currentPage, cursorY } = addFlowBand(payload, currentPage, pages, byRole, pageSetup, band, cursorY, rowIndex, overflow, contentTop, contentBottom, row));
      } else {
        addResolvedBand(payload, currentPage, band, cursorY, rowIndex, overflow, row);
        cursorY += band.height;
      }
      if (band.pageBreakAfter) {
        ({ page: currentPage, cursorY } = addPage(pageSetup, pages, byRole, payload, overflow, contentTop, contentBottom));
      }
    }
  }

  for (const band of byRole.reportFooter) {
    if (band.pageBreakBefore || cursorY + band.height > contentBottom) {
      ({ page: currentPage, cursorY } = addPage(pageSetup, pages, byRole, payload, overflow, contentTop, contentBottom));
    }
    if (isFlowBand(band)) {
      ({ page: currentPage, cursorY } = addFlowBand(payload, currentPage, pages, byRole, pageSetup, band, cursorY, undefined, overflow, contentTop, contentBottom));
    } else {
      addResolvedBand(payload, currentPage, band, cursorY, undefined, overflow);
      cursorY += band.height;
    }
  }

  if (nativeVisibility(payload)) {
    while (pages.length > 1 && pages[pages.length - 1].bands.every((band) => ["pageHeader", "pageFooter"].includes(band.role))) pages.pop();
  }
  return {
    schemaVersion: "reports-v3.1",
    page: pageSetup,
    pages,
    overflow,
    ...(nativeVisibility(payload) ? { visibility: { decisions: [...nativeVisibility(payload).decisions.values()], work: nativeVisibility(payload).work, notices: [...nativeVisibility(payload).notices], ...(nativeVisibility(payload).formatting ? { styleCount: nativeVisibility(payload).styleCount } : {}) } } : {}),
  };
}

function bandVisible(payload, band, row, rowIndex = 0) {
  const visibility = nativeVisibility(payload);
  return !visibility || visibility.visible(band, visibility.context(row), `${band.id}/${rowIndex}`);
}

function prepareNativeBands(payload) {
  const bands = (payload.layout?.bands || []).map(normalizeBand);
  const visibility = nativeVisibility(payload);
  if (!visibility) return bands;
  for (const band of bands) {
    const rows = band.role === "detail" ? detailRows(payload, band.dataset) : [];
    if (band.role === "detail" && band.dataset && !rows.length) continue;
    for (const [index, row] of (rows.length ? rows : [undefined]).entries()) {
      bandVisible(payload, band, row, index);
      const context = visibility.context(row);
      function visit(elements) {
        for (const element of elements) {
          if (element.type === "table") buildNativeTablePlan(payload, element, band.dataset, row, `${band.id}/${index}`);
          else {
            visibility.visible(element, context, `${band.id}/${index}/${element.id}`);
            if (element.elements) visit(element.elements);
          }
        }
      }
      visit(band.elements);
    }
  }
  return bands;
}

export function resolveNativeBinding(payload, binding, row, datasetName) {
  if (!binding) return undefined;
  const path = binding.trim();
  if (!path) return undefined;
  if (path === "pageNumber") return undefined;
  if (path.startsWith("row.")) return getPath(row, path.slice(4));
  if (row && hasPath(asRecord(row), path)) return getPath(row, path);
  if (datasetName) {
    const dataset = getDataset(payload, datasetName);
    if (hasPath(asRecord(dataset), path)) return getPath(dataset, path);
  }
  if (path.startsWith("datasets.")) return getPath(payload.datasets, path.slice("datasets.".length));
  return getPath(payload.datasets, path) ?? getPath(payload, path);
}

export function formatNativeValue(value, format) {
  if (value === null || value === undefined || value === "") return "";
  if (format === "money") {
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString("en-US", { style: "currency", currency: "USD" }) : String(value);
  }
  if (format === "number") {
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString("en-US") : String(value);
  }
  if (format === "date") {
    const calendar = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
    if (calendar) return `${Number(calendar[2])}/${Number(calendar[3])}/${calendar[1]}`;
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString("en-US");
  }
  return String(value);
}

const imageCrcTable = Uint32Array.from({ length: 256 }, (_, value) => {
  for (let bit = 0; bit < 8; bit++) value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
  return value >>> 0;
});

function validInlinePng(encoded) {
  let bytes;
  try { bytes = atob(encoded); } catch { return false; }
  if (bytes.slice(0, 8) !== "\x89PNG\r\n\x1a\n") return false;
  const uint32 = offset => ((bytes.charCodeAt(offset) << 24) | (bytes.charCodeAt(offset + 1) << 16) |
    (bytes.charCodeAt(offset + 2) << 8) | bytes.charCodeAt(offset + 3)) >>> 0;
  let offset = 8, hasPixels = false;
  while (offset + 12 <= bytes.length) {
    const length = uint32(offset), type = bytes.slice(offset + 4, offset + 8);
    if (length > bytes.length - offset - 12) return false;
    if (offset === 8) {
      if (type !== "IHDR" || length !== 13) return false;
      const width = uint32(16), height = uint32(20);
      if (!width || !height) return false;
    } else if (type === "IHDR") return false;
    let crc = 0xffffffff;
    for (let index = offset + 4; index < offset + 8 + length; index++) crc = imageCrcTable[(crc ^ bytes.charCodeAt(index)) & 255] ^ (crc >>> 8);
    if (((crc ^ 0xffffffff) >>> 0) !== uint32(offset + 8 + length)) return false;
    if (type === "IDAT") hasPixels = hasPixels || length > 0;
    offset += length + 12;
    if (type === "IEND") return length === 0 && offset === bytes.length && hasPixels;
  }
  return false;
}

export function resolveNativeImageSource(payload, reference) {
  const assets = asRecord(payload.assets);
  const key = String(reference || "").replace(/^assets\./, "");
  const asset = asRecord(assets[key] || Object.values(assets).find((candidate) => asRecord(candidate).assetId === key));
  if (!key || asset.available !== true || typeof asset.url !== "string" || !asset.url) return { unavailable: true, error: `Image asset "${key}" is missing or unavailable.` };
  const inline = /^data:(image\/(?:png|jpe?g));base64,([A-Za-z0-9+/]+={0,2})$/i.exec(asset.url);
  if (inline) {
    const bytes = inline[2].length * 3 / 4 - (inline[2].endsWith("==") ? 2 : inline[2].endsWith("=") ? 1 : 0);
    const signature = /^image\/png$/i.test(inline[1]) ? inline[2].startsWith("iVBORw0KGgo") : inline[2].startsWith("/9j/");
    if (inline[2].length % 4 !== 0 || bytes > 2 * 1024 * 1024 || !signature || (asset.contentType && String(asset.contentType).toLowerCase() !== inline[1].toLowerCase())) return { error: `Image asset "${key}" is invalid, mismatched, or exceeds the 2 MiB limit.` };
    if (/^image\/png$/i.test(inline[1]) && !validInlinePng(inline[2])) return { error: `Image asset "${key}" has invalid PNG checksums or structure.` };
    return { src: asset.url };
  }
  try {
    const url = new URL(asset.url);
    if (url.protocol === "https:" && !url.username && !url.password && !url.hash && (!url.port || url.port === "443") &&
        /\.blob\.core\.windows\.net$/i.test(url.hostname) && url.pathname.startsWith("/images/company-logos/")) return { src: asset.url };
  } catch {
    // Non-URL asset references are not browser-fetchable native images.
  }
  return { error: `Image asset "${key}" must use a bounded PNG/JPEG data URI or an approved company-logo URL.` };
}

export function measureNativeTextHeight(value, width, style = {}) {
  return estimateTextHeight(value, width, style);
}

function addPage(pageSetup, pages, byRole, payload, overflow, contentTop, contentBottom) {
  const page = createPage(pageSetup, pages.length + 1);
  pages.push(page);
  addFixedBands(payload, page, byRole.pageHeader, pageSetup.printableArea.y, overflow);
  addFixedBands(payload, page, byRole.pageFooter, contentBottom, overflow);
  return { page, cursorY: contentTop };
}

function addFixedBands(payload, page, bands, startY, overflow) {
  let y = startY;
  bands.forEach((band) => {
    addResolvedBand(payload, page, band, y, undefined, overflow);
    y += band.height;
  });
}

function addResolvedBand(payload, page, band, y, rowIndex, overflow, row) {
  const bounds = { x: page.printableArea.x, y, width: page.printableArea.width, height: band.height };
  if (band.height > page.printableArea.height) {
    overflow.push({
      bandId: band.id,
      elementId: band.id,
      pageNumber: page.pageNumber,
      message: `Band "${band.id}" height exceeds the printable page area.`,
    });
  }

  const resolvedElements = band.elements.map((element) => resolveNativeElement(payload, element, {
    bandId: band.id,
    datasetName: band.dataset,
    pageNumber: page.pageNumber,
    parentAbsolute: bounds,
    parentContent: bounds,
    row,
    overflow,
    depth: 0,
    ancestors: [],
    visibilityInstance: `${band.id}/${rowIndex ?? 0}`,
  })).filter(Boolean);
  flattenResolvedElements(resolvedElements).forEach((element) => {
    if (elementExtendsBeyond(element.absolute, bounds)) {
      overflow.push({
        bandId: band.id,
        elementId: element.id,
        pageNumber: page.pageNumber,
        message: `Element "${element.id}" extends outside band "${band.id}" fixed height.`,
      });
      element.overflow = true;
    }
  });
  const resolvedBand = {
    id: band.id,
    role: band.role,
    pageNumber: page.pageNumber,
    rowIndex,
    bounds,
    elements: resolvedElements,
  };
  page.bands.push(resolvedBand);
}

function addFlowBand(payload, currentPage, pages, byRole, pageSetup, band, cursorY, rowIndex, overflow, contentTop, contentBottom, row) {
  const flowIssue = validateFlowBandShape(band);
  if (flowIssue) {
    overflow.push({ bandId: band.id, elementId: band.id, pageNumber: currentPage.pageNumber, message: flowIssue });
    addResolvedBand(payload, currentPage, { ...band, sizing: "fixed" }, cursorY, rowIndex, overflow, row);
    return { page: currentPage, cursorY: cursorY + band.height };
  }

  const table = band.elements.find((element) => element.type === "table");
  const prefixElements = band.elements.filter((element) => element.type !== "table");
  const tablePlan = buildNativeTablePlan(payload, table, band.dataset, row, `${band.id}/${rowIndex ?? 0}`);
  if (nativeVisibility(payload) && (!tablePlan.reportVisible || !tablePlan.flowRows.length)) {
    addResolvedBand(payload, currentPage, { ...band, elements: prefixElements }, cursorY, rowIndex, overflow, row);
    return { page: currentPage, cursorY: cursorY + band.height };
  }
  const pageContentHeight = Math.max(0, contentBottom - contentTop);
  let nextPage = currentPage;
  let nextCursorY = cursorY;
  let entryCursor = 0;
  let fragmentIndex = 0;
  let firstFragment = true;
  let safety = 0;

  for (const flowEntry of tablePlan.flowRows) {
    if (flowEntry.height + (tablePlan.header.reportVisible === false ? 0 : tablePlan.header.height) > pageContentHeight) {
      overflow.push({
        bandId: band.id,
        elementId: table.id,
        pageNumber: nextPage.pageNumber,
        message: `Table "${table.id}" ${flowEntry.kind} ${flowEntry.rowIndex !== undefined && flowEntry.rowIndex >= 0 ? flowEntry.rowIndex + 1 : ""} is too tall to fit on a page with repeated headers.`.replace(/\s+is/, " is"),
      });
    }
  }

  while (entryCursor < tablePlan.flowRows.length) {
    safety += 1;
    if (safety > Math.max(20, tablePlan.flowRows.length * 3 + 10)) {
      overflow.push({
        bandId: band.id,
        elementId: table.id,
        pageNumber: nextPage.pageNumber,
        message: `Table "${table.id}" pagination could not make progress.`,
      });
      break;
    }
    if (tablePlan.flowRows[entryCursor]?.kind === "groupSpacer" && nextCursorY <= contentTop) {
      entryCursor += 1;
      continue;
    }
    const reservedTop = firstFragment ? tablePlan.y : 0;
    const leadingRows = buildFragmentLeadingRows(tablePlan, entryCursor, fragmentIndex);
    const leadingHeight = leadingRows.reduce((sum, item) => sum + item.height, 0);
    const nextEntry = tablePlan.flowRows[entryCursor];
    const minimumTableHeight = leadingHeight + positiveNumber(nextEntry?.height, 0);
    if (nextCursorY + reservedTop + minimumTableHeight > contentBottom && nextCursorY > contentTop) {
      ({ page: nextPage, cursorY: nextCursorY } = addPage(pageSetup, pages, byRole, payload, overflow, contentTop, contentBottom));
      firstFragment = false;
      continue;
    }

    const fragmentPageNumber = nextPage.pageNumber;
    const bandY = nextCursorY;
    const tableY = bandY + reservedTop;
    const availableHeight = Math.max(0, contentBottom - bandY - reservedTop);
    const fragmentRows = [];
    let usedTableHeight = 0;

    for (const leadingRow of leadingRows) {
      fragmentRows.push({ ...leadingRow, y: usedTableHeight });
      usedTableHeight += leadingRow.height;
    }

    const placedBeforeLoop = fragmentRows.length;
    while (entryCursor < tablePlan.flowRows.length) {
      const currentEntry = tablePlan.flowRows[entryCursor];
      const nextFlowEntry = tablePlan.flowRows[entryCursor + 1];
      const nonHeaderRowsPlaced = fragmentRows.length > placedBeforeLoop;
      if (currentEntry.kind === "groupSpacer" && usedTableHeight + currentEntry.height > availableHeight) {
        if (usedTableHeight > leadingHeight || nextCursorY > contentTop) break;
        entryCursor += 1;
        continue;
      }
      if (shouldBreakBeforeFlowEntry(tablePlan, currentEntry, nextFlowEntry, usedTableHeight, availableHeight, nonHeaderRowsPlaced, nextCursorY, contentTop, pageContentHeight)) {
        break;
      }
      if (usedTableHeight + currentEntry.height > availableHeight && nonHeaderRowsPlaced) break;
      if (usedTableHeight + currentEntry.height > availableHeight && currentEntry.height + leadingHeight <= pageContentHeight && nextCursorY > contentTop) break;
      fragmentRows.push({ ...currentEntry, y: usedTableHeight });
      usedTableHeight += currentEntry.height;
      entryCursor += 1;
      if (usedTableHeight >= availableHeight) break;
    }

    if (fragmentRows.length === placedBeforeLoop && entryCursor < tablePlan.flowRows.length && nextCursorY > contentTop) {
      ({ page: nextPage, cursorY: nextCursorY } = addPage(pageSetup, pages, byRole, payload, overflow, contentTop, contentBottom));
      firstFragment = false;
      continue;
    }

    const bandHeight = Math.max(reservedTop + usedTableHeight, firstFragment ? band.height : usedTableHeight);
    const bounds = { x: nextPage.printableArea.x, y: bandY, width: nextPage.printableArea.width, height: bandHeight };
    const prefix = firstFragment ? prefixElements.map((element) => resolveNativeElement(payload, element, {
      bandId: band.id,
      datasetName: band.dataset,
      pageNumber: fragmentPageNumber,
      parentAbsolute: bounds,
      parentContent: bounds,
      row,
      overflow,
      depth: 0,
      ancestors: [],
      visibilityInstance: `${band.id}/${rowIndex ?? 0}`,
    })).filter(Boolean) : [];

    const tableFragment = resolveNativeTableFragment(tablePlan, {
      table,
      band,
      row,
      rowIndex,
      pageNumber: fragmentPageNumber,
      parentAbsolute: bounds,
      tableAbsolute: { x: bounds.x + tablePlan.x, y: tableY, width: tablePlan.width, height: usedTableHeight },
      fragmentIndex,
      isContinuation: fragmentIndex > 0,
      rows: fragmentRows,
    });

    nextPage.bands.push({
      id: band.id,
      role: band.role,
      pageNumber: fragmentPageNumber,
      rowIndex,
      fragmentIndex,
      flow: true,
      bounds,
      elements: [...prefix, tableFragment],
    });

    if (bandHeight > pageContentHeight) {
      overflow.push({
        bandId: band.id,
        elementId: table.id,
        pageNumber: fragmentPageNumber,
        message: `Flow band "${band.id}" content exceeds the usable page area.`,
      });
    }

    nextCursorY += bandHeight;
    fragmentIndex += 1;
    firstFragment = false;

    if (entryCursor < tablePlan.flowRows.length) {
      ({ page: nextPage, cursorY: nextCursorY } = addPage(pageSetup, pages, byRole, payload, overflow, contentTop, contentBottom));
    }
  }

  return { page: nextPage, cursorY: nextCursorY };
}

function buildFragmentLeadingRows(tablePlan, entryCursor, fragmentIndex) {
  const rows = [];
  const nextEntry = tablePlan.flowRows[entryCursor];
  const repeatedGroupHeader = fragmentIndex > 0 ? repeatedGroupHeaderForEntry(tablePlan, nextEntry) : null;
  if (repeatedGroupHeader) rows.push(repeatedGroupHeader);
  if ((tablePlan.repeatHeader || (tablePlan.analysisColumns && fragmentIndex === 0)) && tablePlan.header.reportVisible !== false) rows.push(tablePlan.header);
  return rows;
}

function repeatedGroupHeaderForEntry(tablePlan, entry) {
  if (!entry || entry.kind !== "detailRow" || !entry.groupId) return null;
  const group = tablePlan.groupHeaders.get(entry.groupId);
  if (!group?.repeatOnContinuation) return null;
  return { ...group.row, generated: true, repeated: true, sourceId: `${group.row.sourceId}:repeat` };
}

function shouldBreakBeforeFlowEntry(tablePlan, entry, nextEntry, usedHeight, availableHeight, rowsPlaced, cursorY, contentTop, pageContentHeight) {
  if (!entry) return false;
  const canBreak = rowsPlaced || cursorY > contentTop;
  if (!canBreak) return false;
  if (entry.pageBreakBefore === true) return true;
  if (entry.kind === "groupHeader" && nextEntry?.kind === "detailRow" && nextEntry.groupId === entry.groupId) {
    const keepWithFirstRow = tablePlan.groupPagination.get(entry.groupId)?.keepHeaderWithFirstRow !== false;
    if (keepWithFirstRow && entry.height + nextEntry.height <= pageContentHeight && usedHeight + entry.height + nextEntry.height > availableHeight) {
      return true;
    }
  }
  if (entry.kind === "detailRow" && nextEntry?.kind === "groupFooter" && nextEntry.groupId === entry.groupId) {
    const keepWithLastRow = tablePlan.groupPagination.get(entry.groupId)?.keepFooterWithLastRow !== false;
    if (keepWithLastRow && entry.height + nextEntry.height <= pageContentHeight && usedHeight + entry.height + nextEntry.height > availableHeight) {
      return true;
    }
  }
  return false;
}

function flattenResolvedElements(elements) {
  return elements.flatMap((element) => [element, ...flattenResolvedElements(element.elements || [])]);
}

function resolveNativeElement(payload, element, context, overrides = {}) {
  const visibility = nativeVisibility(payload);
  if (visibility && !visibility.visible(element, visibility.context(context.row), `${context.visibilityInstance}/${element.id}`)) return null;
  const x = numberOrDefault(overrides.x, element.x);
  const y = numberOrDefault(overrides.y, element.y);
  const width = positiveNumber(overrides.width, element.width);
  const height = positiveNumber(overrides.height, element.height);
  const absolute = {
    x: context.parentContent.x + x,
    y: context.parentContent.y + y,
    width,
    height,
  };
  const resolvedX = absolute.x - context.parentAbsolute.x;
  const resolvedY = absolute.y - context.parentAbsolute.y;
  const base = {
    ...element,
    ...presentationStyle(payload, element, visibility?.context(context.row), `${context.visibilityInstance}/${element.id}`, element.style),
    x: resolvedX,
    y: resolvedY,
    width,
    height,
    sourceBounds: { x: element.x, y: element.y, width: element.width, height: element.height },
    absolute,
    parentId: context.ancestors[context.ancestors.length - 1],
    depth: context.depth,
  };

  if (element.type === "container") {
    const children = Array.isArray(element.elements) ? element.elements : [];
    const padding = normalizeSpacing(element.padding);
    const content = {
      x: absolute.x + padding.left,
      y: absolute.y + padding.top,
      width: Math.max(0, absolute.width - padding.left - padding.right),
      height: Math.max(0, absolute.height - padding.top - padding.bottom),
    };
    if (context.depth >= NATIVE_CONTAINER_MAX_DEPTH) {
      context.overflow.push({
        bandId: context.bandId,
        elementId: element.id,
        pageNumber: context.pageNumber,
        message: `Container "${element.id}" exceeds the supported nesting depth of ${NATIVE_CONTAINER_MAX_DEPTH}.`,
      });
      return { ...base, elements: [], padding, layout: normalizeContainerLayout(element.layout), overflow: true };
    }
    if (context.ancestors.includes(element.id)) {
      context.overflow.push({
        bandId: context.bandId,
        elementId: element.id,
        pageNumber: context.pageNumber,
        message: `Container "${element.id}" creates a cyclic native layout relationship.`,
      });
      return { ...base, elements: [], padding, layout: normalizeContainerLayout(element.layout), overflow: true };
    }
    const layout = normalizeContainerLayout(element.layout);
    const childPlans = resolveContainerChildren(payload, children, layout, {
      ...context,
      parentAbsolute: absolute,
      parentContent: content,
      depth: context.depth + 1,
      ancestors: [...context.ancestors, element.id],
    });
    const childOverflow = childPlans.some((child) => elementExtendsBeyond(child.absolute, content) || child.overflow);
    if (childOverflow && element.overflow !== "visible") {
      context.overflow.push({
        bandId: context.bandId,
        elementId: element.id,
        pageNumber: context.pageNumber,
        message: `Container "${element.id}" has child content outside its content bounds.`,
      });
    }
    return {
      ...base,
      padding,
      gap: layout.gap,
      layout,
      elements: childPlans,
      overflow: childOverflow && element.overflow !== "visible",
    };
  }

  if (element.type === "table") {
    context.overflow.push({
      bandId: context.bandId,
      elementId: element.id,
      pageNumber: context.pageNumber,
      message: `Table "${element.id}" requires a flow-capable band for native pagination.`,
    });
    return { ...base, value: "", overflow: true };
  }

  if (element.type === "image") {
    return {
      ...base,
      value: "",
      asset: element.asset,
      fit: ["cover", "stretch", "contain"].includes(element.fit) ? element.fit : "contain",
      lockAspectRatio: element.lockAspectRatio !== false,
      overflow: false,
    };
  }

  const { rawValue, value } = resolveNativeValueContent(payload, element, context.row, context.datasetName);
  const border = base.conditionalStyle ? base.style?.borderWidth ?? (base.style?.borderColor ? 1 : 0) : 0;
  const elementOverflow = estimateTextHeight(value, width - border * 2, base.style, base.conditionalStyle) + border * 2 > height;
  if (elementOverflow) {
    context.overflow.push({
      bandId: context.bandId,
      elementId: element.id,
      pageNumber: context.pageNumber,
      message: `Element "${element.id}" text exceeds its ${height}pt height.`,
    });
  }
  return { ...base, rawValue, value, overflow: elementOverflow };
}

function resolveContainerChildren(payload, children, layout, context) {
  const visibility = nativeVisibility(payload);
  const originalChildren = children;
  if (visibility) children = children.filter((child) => visibility.visible(child, visibility.context(context.row), `${context.visibilityInstance}/${child.id}`));
  if (layout.mode === "columns") {
    if (visibility && layout.columns.length) layout = { ...layout, columns: originalChildren.flatMap((child, index) => children.includes(child) ? [layout.columns[index] ?? "1fr"] : []) };
    return resolveColumnChildren(payload, children, layout, context);
  }
  if (layout.mode === "vertical") {
    let cursorY = 0;
    return children.map((child) => {
      const height = positiveNumber(child.height, 18);
      const resolved = resolveNativeElement(payload, child, context, {
        x: 0,
        y: cursorY,
        width: context.parentContent.width,
        height,
      });
      cursorY += height + layout.gap;
      return resolved;
    });
  }
  return children.map((child) => resolveNativeElement(payload, child, context));
}

function resolveColumnChildren(payload, children, layout, context) {
  const gapTotal = Math.max(0, children.length - 1) * layout.gap;
  const availableWidth = Math.max(0, context.parentContent.width - gapTotal);
  const widths = resolveColumnWidths(layout.columns, children.length, availableWidth, Boolean(nativeVisibility(payload)));
  let cursorX = 0;
  return children.map((child, index) => {
    const width = widths[index] ?? (children.length ? availableWidth / children.length : availableWidth);
    const resolved = resolveNativeElement(payload, child, context, {
      x: cursorX,
      y: 0,
      width,
      height: positiveNumber(child.height, context.parentContent.height),
    });
    cursorX += width + layout.gap;
    return resolved;
  });
}

function resolveColumnWidths(columns, childCount, availableWidth, preserveFixed = false) {
  const specs = Array.from({ length: childCount }, (_, index) => columns[index]?.width ?? columns[index] ?? "1fr");
  let fixed = 0;
  let fr = 0;
  const parsed = specs.map((spec) => {
    if (typeof spec === "number" && Number.isFinite(spec)) {
      fixed += Math.max(0, spec);
      return { type: "fixed", value: Math.max(0, spec) };
    }
    const text = String(spec || "1fr").trim();
    if (text.endsWith("%")) {
      const value = Math.max(0, (Number.parseFloat(text) / 100) * availableWidth);
      fixed += value;
      return { type: "fixed", value };
    }
    if (text.endsWith("fr")) {
      const value = Math.max(0, Number.parseFloat(text) || 1);
      fr += value;
      return { type: "fr", value };
    }
    const value = Math.max(0, Number.parseFloat(text) || 0);
    fixed += value;
    return { type: "fixed", value };
  });
  if (!fr && fixed > 0) {
    if (preserveFixed) return parsed.map((item) => item.value);
    const scale = Math.max(0, availableWidth) / fixed;
    return parsed.map((item) => item.value * scale);
  }
  const remaining = Math.max(0, availableWidth - fixed);
  return parsed.map((item) => item.type === "fr" ? (fr ? remaining * item.value / fr : 0) : item.value);
}

function buildNativeTablePlan(payload, table, bandDatasetName, scopedRow, instance = "") {
  if ((table.groups?.length || 0) > 1) throw nativeContentError("Multiple native grouping levels are not supported yet.");
  const visibility = nativeVisibility(payload);
  const identity = `${instance}/${table.id}`;
  if (visibility?.tables.has(identity)) return visibility.tables.get(identity);
  const width = positiveNumber(table.width, 72);
  const allColumns = normalizeNativeTableColumns(table.columns, width);
  let columns = visibleNativeTableColumns(allColumns, width);
  const analysisColumns = columns;
  const tableStyle = { ...DEFAULT_TABLE_STYLE, ...asRecord(table.style) };
  const headerStyle = { ...tableStyle, ...asRecord(table.header?.style) };
  const rowStyle = { ...tableStyle, ...asRecord(table.row?.style) };
  const footerStyle = { ...tableStyle, ...asRecord(table.footer?.style) };
  if (visibility?.formatting) {
    Object.assign(headerStyle, { backgroundColor: table.header?.style?.backgroundColor ?? tableStyle.headerBackgroundColor, bold: table.header?.style?.bold ?? tableStyle.bold ?? true, fontSize: table.header?.style?.fontSize ?? tableStyle.headerFontSize, borderWidth: headerStyle.borderWidth ?? 0.5 });
    Object.assign(footerStyle, { backgroundColor: table.footer?.style?.backgroundColor ?? tableStyle.footerBackgroundColor, bold: table.footer?.style?.bold ?? tableStyle.bold ?? true, fontSize: table.footer?.style?.fontSize ?? tableStyle.footerFontSize, borderWidth: footerStyle.borderWidth ?? 0.5 });
  }
  const rowPadding = normalizeSpacing(table.row?.padding || table.cellPadding || DEFAULT_TABLE_PADDING);
  const headerPadding = normalizeSpacing(table.header?.padding || table.cellPadding || DEFAULT_TABLE_PADDING);
  const footerPadding = normalizeSpacing(table.footer?.padding || table.cellPadding || DEFAULT_TABLE_PADDING);
  const groupHeaderPadding = normalizeSpacing(table.groupHeader?.padding || table.cellPadding || DEFAULT_TABLE_PADDING);
  const groupFooterPadding = normalizeSpacing(table.groupFooter?.padding || table.cellPadding || DEFAULT_TABLE_PADDING);
  const rowsInfo = tableRowsInfo(payload, table.dataset || bandDatasetName, scopedRow);
  const typed = nativeTypedExecution(payload);
  const rows = rowsInfo.rows.map((source, index) => typed?.hasTable(table)
    ? { source, row: { ...asRecord(source), ...typed.table(table, source) }, rowIndex: index, precomputed: true }
    : buildEvaluationRow(payload, source, allColumns, table.dataset || bandDatasetName, index))
    .filter(info => !typed || typed.retain(table, info.source, info.row));
  const aggregateContext = buildAggregateContext(rows);
  const context = visibility ? {
    ...visibility.context(undefined, getDataset(payload, table.dataset || bandDatasetName)),
    reportRows: aggregateContext.reportRows,
    calculatedFields: typed?.hasTable(table) ? {} : Object.fromEntries(allColumns.filter((column) => column.expr || column.calculation).map((column) => [column.id, column.expr || column.calculation])),
  } : undefined;
  const reportVisible = !visibility || visibility.visible(table, context, identity);
  if (visibility) {
    const eligible = allColumns.filter((column) => visibility.visible(column, context, `${identity}/column/${column.id}`));
    columns = eligible.length ? visibleNativeTableColumns(eligible, width) : [];
    if (!reportVisible || !columns.length) visibility.notices.add("A Report table or its columns are hidden by presentation rules; all statically eligible Data records and columns are retained.");
  }
  const repeatHeader = table.header?.repeat !== false && table.pagination?.repeatHeader !== false;
  const headerCells = columns.map((column) => ({ columnId: column.id, value: column.label,
    ...presentationStyle(payload, table.header, context, `${identity}/header`, { ...headerStyle, horizontalAlign: column.headerHorizontalAlign || column.horizontalAlign || "center" }) }));
  const header = {
    ...(visibility ? { reportVisible: visibility.visible(table.header, context, `${identity}/header`) } : {}),
    kind: "tableHeader",
    sourceId: `${table.id}:tableHeader`,
    cells: headerCells,
    ...measureNativeTableRow(
    headerCells,
    columns,
    headerPadding,
    { ...headerStyle, fontSize: positiveNumber(headerStyle.headerFontSize, positiveNumber(headerStyle.fontSize, 10)), bold: headerStyle.bold !== false },
    positiveNumber(table.header?.minHeight, 0),
  ) };
  const groupDefinition = normalizeTableGroup(table.groups?.[0]);
  const sortDescriptors = normalizeTableSort(table.sort);
  const sortedRows = sortNativeRows(rows, sortDescriptors);
  const evaluationRows = typed?.hasTable(table) ? new Map(rows.map(info => [info.rowIndex, info.row])) : undefined;
  const detailRows = sortedRows.map((row) => buildDetailTableRow(payload, visibility ? allColumns : columns, row, table.dataset || bandDatasetName, rowStyle, rowPadding, table));
  const presentDetail = (detail, group) => {
    if (!visibility) return detail;
    const rowContext = { ...context, detail: evaluationRows?.get(detail.rowIndex) || detail.source, sourceIndex: detail.rowIndex, ...group };
    const visible = visibility.visible(table.row, rowContext, `${identity}/row/${detail.rowIndex}`);
    if (visibility.formatting) visibility.style(table.row, rowContext, `${identity}/row/${detail.rowIndex}`, rowStyle);
    const cells = detail.cells.map((cell) => {
      const column = allColumns.find((candidate) => candidate.id === cell.columnId);
      const cellContext = { ...rowContext, cell: { value: cell.rawValue } };
      const cellIdentity = `${identity}/row/${detail.rowIndex}/cell/${cell.columnId}`;
      const painted = visibility.visible(column.detail, cellContext, cellIdentity);
      const base = visibility.formatting ? { ...cell.style, bold: cell.style.bold ?? false, borderWidth: cell.style.borderWidth ?? 0.5,
        horizontalAlign: cell.style.horizontalAlign || (["money", "number"].includes(cell.format) ? "right" : "left"),
        borderColor: column.style?.borderColor ?? table.row?.style?.rowBorderColor ?? table.row?.style?.borderColor ?? table.style?.rowBorderColor ?? table.style?.borderColor ?? tableStyle.rowBorderColor } : cell.style;
      return { ...cell, ...presentationStyle(payload, column.detail, cellContext, cellIdentity, base, `${identity}/row/${detail.rowIndex}`), ...(painted ? {} : { reportVisible: false, value: "" }) };
    }).filter((cell) => columns.some((column) => column.id === cell.columnId));
    return { ...detail, reportVisible: visible, cells, ...measureNativeTableRow(cells, columns, rowPadding, rowStyle, positiveNumber(table.row?.minHeight, 0)) };
  };
  const defaultEmptyText = nativeTableEmptyText(table.dataset || bandDatasetName, rowsInfo);
  const configuredEmptyText = typeof table.emptyText === "string" && table.emptyText.trim() && table.emptyText.trim() !== "No records." ? table.emptyText : "";
  const emptyText = configuredEmptyText || defaultEmptyText;
  const emptyRow = {
    kind: "empty",
    rowIndex: -1,
    source: {},
    sourceId: `${table.id}:empty`,
    cells: [{ columnId: columns[0]?.id || "empty", value: emptyText, style: rowStyle, colSpan: columns.length || 1 }],
    ...measureNativeTableRow([{ columnId: columns[0]?.id || "empty", value: emptyText, style: rowStyle, colSpan: columns.length || 1 }], columns, rowPadding, rowStyle, positiveNumber(table.row?.minHeight, 0)),
    empty: true,
  };
  let footer = table.footer?.enabled === false && !visibility ? null : buildNativeTableFooter(table, columns, allColumns, detailRows, footerPadding, footerStyle, aggregateContext, "report", payload, context, identity);
  if (visibility && (!visibility.visible(table.footer, context, `${identity}/footer`) || table.footer?.enabled === false)) footer = null;
  const groupedRows = groupDefinition ? buildGroupedFlowRows({
    payload,
    table,
    columns,
    allColumns,
    rows: sortedRows,
    detailRows,
    groupDefinition,
    aggregateContext,
    groupHeaderPadding,
    groupFooterPadding,
    tableStyle,
    visibilityContext: context, identity, presentDetail,
  }) : { flowRows: detailRows.map((entry) => presentDetail(entry)).filter((entry) => entry.reportVisible !== false), groupHeaders: new Map(), groupPagination: new Map() };
  const flowRows = detailRows.length ? groupedRows.flowRows : [emptyRow];
  if (footer) flowRows.push({ ...footer, kind: "tableFooter", sourceId: `${table.id}:tableFooter` });
  const suppressed = detailRows.length - flowRows.filter((entry) => entry.kind === "detailRow").length;
  if (visibility && suppressed > 0) {
    visibility.notices.add(VISIBILITY_SUPPRESSION_NOTICE);
    if (columns.length && reportVisible) {
      const cells = [{ columnId: columns[0].id, colSpan: columns.length, value: VISIBILITY_SUPPRESSION_NOTICE, rawValue: VISIBILITY_SUPPRESSION_NOTICE, style: { ...tableStyle, fontSize: 9 } }];
      flowRows.push({ kind: "notice", sourceId: `${identity}/suppression`, cells, ...measureNativeTableRow(cells, columns, rowPadding, tableStyle) });
    }
  }
  const analysisOrder = groupedRows.analysisOrder || sortedRows;
  const detailsByIndex = new Map(detailRows.map((detail) => [detail.rowIndex, detail]));
  const plan = {
    id: table.id,
    x: table.x,
    y: table.y,
    width,
    columns,
    rows: detailRows,
    header,
    footer,
    flowRows: visibility && (!reportVisible || !columns.length) ? [] : flowRows,
    groupHeaders: groupedRows.groupHeaders,
    groupPagination: groupedRows.groupPagination,
    repeatHeader,
    style: tableStyle,
    rowPadding,
    headerPadding,
    footerPadding,
    dataset: table.dataset || bandDatasetName,
    ...(visibility ? { reportVisible: reportVisible && columns.length > 0, analysisColumns, analysisRows: analysisOrder.map((row) => {
      const detail = detailsByIndex.get(row.rowIndex);
      return { ...detail, cells: detail.cells.filter((cell) => analysisColumns.some((column) => column.id === cell.columnId)), sourceIndex: detail.rowIndex };
    }) } : {}),
  };
  if (visibility) visibility.tables.set(identity, plan);
  return plan;
}

function buildEvaluationRow(payload, source, columns, datasetName, rowIndex) {
  const row = { ...asRecord(source) };
  for (const column of columns) {
    const expression = column.expr || column.calculation;
    if (expression) row[column.id] = evaluateNativeExpression(expression, { payload, row, datasetName });
  }
  return { source, row, rowIndex };
}

function buildDetailTableRow(payload, columns, rowInfo, datasetName, rowStyle, rowPadding, table) {
  const cells = columns.map((column) => {
    const rawValue = rowInfo.precomputed && (column.expr || column.calculation) ? rowInfo.row[column.id] : column.expr || column.calculation
      ? evaluateNativeExpression(column.expr || column.calculation, { payload, row: rowInfo.row, datasetName })
      : resolveNativeBinding(payload, column.binding || column.key, rowInfo.row, datasetName);
    return {
      columnId: column.id,
      value: formatNativeValue(rawValue, column.format),
      rawValue,
      style: { ...rowStyle, ...asRecord(column.style), horizontalAlign: column.horizontalAlign || column.align || rowStyle.horizontalAlign },
      format: column.format,
    };
  });
  return {
    kind: "detailRow",
    rowIndex: rowInfo.rowIndex,
    source: rowInfo.source,
    sourceId: `${table.id}:row:${rowInfo.rowIndex}`,
    cells,
    ...measureNativeTableRow(cells, columns, rowPadding, rowStyle, positiveNumber(table.row?.minHeight, 0)),
  };
}

function buildGroupedFlowRows({ payload, table, columns, allColumns, rows, detailRows, groupDefinition, aggregateContext, groupHeaderPadding, groupFooterPadding, tableStyle, visibilityContext, identity, presentDetail }) {
  const visibility = nativeVisibility(payload);
  const sorted = [...rows].sort((left, right) => compareGroupRows(left, right, groupDefinition));
  const detailByIndex = new Map(detailRows.map((detail) => [detail.rowIndex, detail]));
  const groups = [];
  for (const rowInfo of sorted) {
    const key = groupKeyForRow(payload, rowInfo.row, groupDefinition, table.dataset);
    const keyId = stableGroupKeyId(key);
    let group = groups.find((candidate) => candidate.keyId === keyId);
    if (!group) {
      group = { id: `${groupDefinition.id}:${groups.length + 1}`, keyId, key, rows: [] };
      groups.push(group);
    }
    group.rows.push(rowInfo);
  }
  aggregateContext.groupRowsById = new Map(groups.map((group) => [group.id, group.rows.map((rowInfo) => rowInfo.row)]));

  const headerStyle = { ...tableStyle, ...asRecord(groupDefinition.header?.style), bold: asRecord(groupDefinition.header?.style).bold !== false };
  const footerStyle = { ...tableStyle, ...asRecord(groupDefinition.footer?.style), bold: asRecord(groupDefinition.footer?.style).bold !== false };
  const flowRows = [];
  const groupHeaders = new Map();
  const groupPagination = new Map();

  groups.forEach((group, groupIndex) => {
    const context = visibility ? { ...visibilityContext, group: { key: group.key }, groupRows: aggregateContext.groupRowsById.get(group.id) } : undefined;
    const headerVisible = !visibility || visibility.visible(groupDefinition.header, context, `${identity}/group/${group.id}/header`);
    const footerVisible = !visibility || visibility.visible(groupDefinition.footer, context, `${identity}/group/${group.id}/footer`);
    const groupHeaderStyle = visibility?.formatting ? { ...headerStyle, backgroundColor: headerStyle.backgroundColor ?? tableStyle.headerBackgroundColor, borderWidth: headerStyle.borderWidth ?? 0.5, fontSize: groupDefinition.header?.style?.fontSize ?? tableStyle.headerFontSize } : headerStyle;
    const groupFooterStyle = visibility?.formatting ? { ...footerStyle, backgroundColor: footerStyle.backgroundColor ?? tableStyle.footerBackgroundColor, borderWidth: footerStyle.borderWidth ?? 0.5, fontSize: groupDefinition.footer?.style?.fontSize ?? tableStyle.footerFontSize } : footerStyle;
    const pagination = asRecord(groupDefinition.pagination);
    const isFirstGroup = groupIndex === 0;
    const isLastGroup = groupIndex === groups.length - 1;
    groupPagination.set(group.id, {
      keepHeaderWithFirstRow: pagination.keepHeaderWithFirstRow !== false,
      keepFooterWithLastRow: pagination.keepFooterWithLastRow !== false,
    });
    const pageBreakBefore = !isFirstGroup && (pagination.pageBreakBefore === true || pagination.pageBreakBeforeGroup === true);
    const spacingBefore = groupSpacing(groupDefinition, "before");
    const startIndex = flowRows.length;
    if (!isFirstGroup && !pageBreakBefore && spacingBefore > 0 && headerVisible) {
      flowRows.push(buildGroupSpacerRow(table, groupDefinition, group, spacingBefore, "before"));
    }
    if (groupDefinition.header?.visible !== false && headerVisible && columns.length) {
      const headerPresentation = presentationStyle(payload, groupDefinition.header, context, `${identity}/group/${group.id}/header`, groupHeaderStyle);
      const headerRow = buildGroupHeaderRow(table, columns, groupDefinition, group, groupHeaderPadding, headerPresentation.style, { pageBreakBefore, presentation: headerPresentation });
      flowRows.push(headerRow);
      groupHeaders.set(group.id, {
        row: headerRow,
        repeatOnContinuation: pagination.repeatHeaderOnContinuation === true || groupDefinition.repeatHeaderOnContinuation === true,
      });
    }
    for (const rowInfo of group.rows) {
      const detail = detailByIndex.get(rowInfo.rowIndex);
      const presented = detail && presentDetail(detail, context);
      if (presented && presented.reportVisible !== false) flowRows.push({ ...presented, groupId: group.id, groupKey: group.key });
    }
    if (groupDefinition.footer?.visible !== false || visibility) {
      const groupContext = { ...aggregateContext, currentGroupId: group.id };
      const footer = buildGroupFooterRow(table, columns, allColumns, groupDefinition, group, groupFooterPadding, groupFooterStyle, groupContext, payload, context, `${identity}/group/${group.id}`);
      if (footerVisible && groupDefinition.footer?.visible !== false && columns.length && (!visibility || footer.cells.length)) flowRows.push(footer);
    }
    const spacingAfter = groupSpacing(groupDefinition, "after");
    if (!isLastGroup && spacingAfter > 0 && footerVisible) {
      flowRows.push(buildGroupSpacerRow(table, groupDefinition, group, spacingAfter, "after"));
    }
    if (visibility && pageBreakBefore) {
      const first = flowRows.slice(startIndex).find((entry) => entry.kind !== "groupSpacer");
      if (first) first.pageBreakBefore = true;
    }
  });

  return { flowRows, groupHeaders, groupPagination, analysisOrder: groups.flatMap((group) => group.rows) };
}

function groupSpacing(groupDefinition, side) {
  const pagination = asRecord(groupDefinition.pagination);
  const key = side === "before" ? "spacingBefore" : "spacingAfter";
  return positiveNumber(groupDefinition[key] ?? pagination[key], 0);
}

function buildGroupSpacerRow(table, groupDefinition, group, height, side) {
  return {
    kind: "groupSpacer",
    groupId: group.id,
    groupKey: group.key,
    sourceId: `${table.id}:${groupDefinition.id}:${group.keyId}:spacing-${side}`,
    cells: [],
    height,
    spacer: true,
  };
}

function normalizeTableGroup(value) {
  const group = asRecord(value);
  if (!Object.keys(group).length) return null;
  const key = asRecord(group.key);
  const field = typeof group.field === "string" ? group.field : typeof key.field === "string" ? key.field : undefined;
  const expr = asExpression(group.expr || key.expr || group.expression || key.expression);
  return {
    id: String(group.id || field || "group"),
    field,
    expr,
    sort: group.sort === "desc" || group.sort === "descending" ? "desc" : "asc",
    label: typeof group.label === "string" ? group.label : undefined,
    format: typeof group.format === "string" ? group.format : undefined,
    spacingBefore: positiveNumber(group.spacingBefore, undefined),
    spacingAfter: positiveNumber(group.spacingAfter, undefined),
    header: { ...asRecord(group.header), visible: asRecord(group.header).visible !== false },
    footer: { ...asRecord(group.footer), visible: Object.keys(asRecord(group.footer)).length > 0 && asRecord(group.footer).visible !== false },
    pagination: asRecord(group.pagination),
    repeatHeaderOnContinuation: group.repeatHeaderOnContinuation === true,
  };
}

function buildAggregateContext(rows, groupDefinition) {
  const reportRows = rows.map((item) => item.row);
  return { reportRows, groupRowsById: new Map(), currentGroupId: undefined };
}

function compareGroupRows(left, right, groupDefinition) {
  const leftKey = groupKeyForRow(null, left.row, groupDefinition);
  const rightKey = groupKeyForRow(null, right.row, groupDefinition);
  const direction = groupDefinition.sort === "desc" ? -1 : 1;
  const result = compareGroupKeys(leftKey, rightKey);
  if (result !== 0) return result * direction;
  return 0;
}

function compareGroupKeys(left, right) {
  const leftMissing = left === null || left === undefined || left === "";
  const rightMissing = right === null || right === undefined || right === "";
  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;
  const leftDate = parseDateValue(left);
  const rightDate = parseDateValue(right);
  if (leftDate !== null && rightDate !== null) return leftDate - rightDate;
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) return leftNumber - rightNumber;
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
}

function parseDateValue(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.getTime();
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
}

function normalizeTableSort(value) {
  if (!Array.isArray(value)) return [];
  return value.map(asRecord).map((descriptor) => {
    const field = typeof descriptor.field === "string" ? descriptor.field.trim() : "";
    if (!field) return null;
    const direction = descriptor.direction === "desc" || descriptor.direction === "descending" ? "desc" : "asc";
    const type = descriptor.type === "date" || descriptor.type === "number" || descriptor.type === "string" ? descriptor.type : "auto";
    const nulls = descriptor.nulls === "first" ? "first" : "last";
    return { field, direction, type, nulls };
  }).filter(Boolean);
}

function sortNativeRows(rows, descriptors) {
  if (!descriptors.length) return rows;
  return [...rows].sort((left, right) => {
    for (const descriptor of descriptors) {
      const result = compareSortValues(getPath(left.row, descriptor.field), getPath(right.row, descriptor.field), descriptor);
      if (result !== 0) return result;
    }
    return left.rowIndex - right.rowIndex;
  });
}

function compareSortValues(left, right, descriptor) {
  const leftMissing = isSortMissing(left, descriptor.type);
  const rightMissing = isSortMissing(right, descriptor.type);
  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return descriptor.nulls === "first" ? -1 : 1;
  if (rightMissing) return descriptor.nulls === "first" ? 1 : -1;

  const direction = descriptor.direction === "desc" ? -1 : 1;
  if (descriptor.type === "date") return (parseDateValue(left) - parseDateValue(right)) * direction;
  if (descriptor.type === "number") return (Number(left) - Number(right)) * direction;
  if (descriptor.type === "auto") return compareGroupKeys(left, right) * direction;
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" }) * direction;
}

function isSortMissing(value, type) {
  if (value === null || value === undefined || value === "") return true;
  if (type === "date") return parseDateValue(value) === null;
  if (type === "number") return !Number.isFinite(Number(value));
  return false;
}

function groupKeyForRow(payload, row, groupDefinition, datasetName) {
  if (!groupDefinition) return undefined;
  if (groupDefinition.expr) return evaluateNativeExpression(groupDefinition.expr, { payload, row, datasetName });
  return getPath(row, groupDefinition.field);
}

function stableGroupKeyId(value) {
  if (value === null || value === undefined || value === "") return "__blank__";
  return `${typeof value}:${String(value).trim().toLowerCase()}`;
}

function buildGroupHeaderRow(table, columns, groupDefinition, group, padding, style, options = {}) {
  const label = String(groupDefinition.header?.label || groupDefinition.label || "Group");
  const valueFormat = groupDefinition.header?.valueFormat || groupDefinition.format;
  const cells = [{
    columnId: columns[0]?.id || "group",
    value: `${label}: ${formatNativeValue(group.key, valueFormat) || "(blank)"}`,
    rawValue: group.key,
    colSpan: columns.length || 1,
    style,
    ...options.presentation,
  }];
  return {
    kind: "groupHeader",
    groupId: group.id,
    groupKey: group.key,
    pageBreakBefore: options.pageBreakBefore === true,
    sourceId: `${table.id}:${groupDefinition.id}:${group.keyId}:header`,
    cells,
    ...measureNativeTableRow(cells, columns, padding, style, positiveNumber(groupDefinition.header?.height || groupDefinition.header?.minHeight, 0)),
    padding,
  };
}

function buildGroupFooterRow(table, columns, allColumns, groupDefinition, group, padding, style, aggregateContext, payload, visibilityContext, identity) {
  const footer = asRecord(groupDefinition.footer);
  const broadIdentity = `${identity}/footer`;
  const broad = presentationStyle(payload, footer, visibilityContext, broadIdentity, style);
  const configuredCells = Array.isArray(footer.cells) ? footer.cells : [];
  const cells = configuredCells.length ? configuredCells.map((cellValue, index) => {
    const cell = asRecord(cellValue);
    const column = allColumns.find((candidate) => candidate.id === String(cell.columnId || "")) || columns[index];
    const aggregate = normalizeAggregateSpec(cell.aggregate, column, "group");
    const rawValue = aggregate ? evaluateAggregate(aggregate, aggregateContext) : cell.value;
    const visible = !nativeVisibility(payload) || nativeVisibility(payload).visible(cell, { ...visibilityContext, cell: { value: rawValue } }, `${identity}/footer/cell/${cell.id || index}`);
    const projected = projectTableCellToVisibleColumns(cell, index, allColumns, columns);
    if (!projected) return null;
    const columnId = projected.columnId;
    return {
      columnId,
      colSpan: projected.colSpan,
      value: visible ? formatNativeValue(rawValue, cell.format || aggregate?.format || column?.format) : "",
      ...(visible ? {} : { reportVisible: false }),
      rawValue,
      format: cell.format || aggregate?.format || column?.format,
      ...presentationStyle(payload, cell, { ...visibilityContext, cell: { value: rawValue } }, `${identity}/footer/cell/${cell.id || index}`,
        { ...style, ...asRecord(cell.style), horizontalAlign: cell.horizontalAlign || (nativeVisibility(payload)?.formatting ? cell.style?.horizontalAlign : undefined) || column?.horizontalAlign || style.horizontalAlign || (nativeVisibility(payload)?.formatting && ["money", "number"].includes(cell.format || aggregate?.format || column?.format) ? "right" : undefined) }, broadIdentity),
    };
  }).filter(Boolean) : [{
    columnId: columns[0]?.id || "group",
    value: String(footer.label || "Subtotal"),
    colSpan: columns.length || 1,
    ...broad,
  }];
  return {
    kind: "groupFooter",
    groupId: group.id,
    groupKey: group.key,
    sourceId: `${table.id}:${groupDefinition.id}:${group.keyId}:footer`,
    cells,
    ...measureNativeTableRow(cells, columns, padding, style, positiveNumber(footer.height || footer.minHeight, 0)),
    padding,
  };
}

function normalizeNativeTableColumns(columns, tableWidth) {
  const source = Array.isArray(columns) ? columns : [];
  const count = Math.max(1, source.length);
  const gaplessWidth = Math.max(0, tableWidth);
  const widths = resolveColumnWidths(source.map((columnValue) => {
    const column = asRecord(columnValue);
    return column.visible === false || column.hidden === true ? 0 : column.width ?? "1fr";
  }), count, gaplessWidth);
  return source.length ? source.map((columnValue, index) => {
    const column = asRecord(columnValue);
    const id = String(column.id || column.key || column.binding || `column${index + 1}`);
    return {
      id,
      ...(column.conditions !== undefined ? { conditions: column.conditions } : {}),
      ...(column.detail !== undefined ? { detail: column.detail } : {}),
      key: typeof column.key === "string" ? column.key : undefined,
      binding: typeof column.binding === "string" ? column.binding : typeof column.key === "string" ? column.key : undefined,
      label: String(column.label || column.heading || id),
      format: typeof column.format === "string" ? column.format : undefined,
      expr: asExpression(column.expr || column.calculation),
      calculation: asExpression(column.calculation),
      width: widths[index] ?? gaplessWidth / count,
      x: widths.slice(0, index).reduce((sum, value) => sum + value, 0),
      horizontalAlign: column.horizontalAlign || column.align,
      headerHorizontalAlign: column.headerHorizontalAlign,
      style: asRecord(column.style),
      visible: column.visible === false || column.hidden === true ? false : undefined,
    };
  }) : [{
    id: "value",
    key: "value",
    binding: "value",
    label: "Value",
    width: gaplessWidth,
    x: 0,
  }];
}

function visibleNativeTableColumns(columns, tableWidth) {
  const visible = columns.filter((column) => column.visible !== false);
  const source = visible.length ? visible : columns.slice(0, 1);
  const total = source.reduce((sum, column) => sum + positiveNumber(column.width, 0), 0);
  const scale = total > 0 ? Math.max(0, tableWidth) / total : 1;
  let x = 0;
  return source.map((column, index) => {
    const width = index === source.length - 1
      ? Math.max(0, tableWidth - x)
      : Math.max(0, positiveNumber(column.width, tableWidth / source.length) * scale);
    const next = { ...column, width, x };
    x += width;
    return next;
  });
}

function projectTableCellToVisibleColumns(cell, index, allColumns, visibleColumns) {
  if (!visibleColumns.length) return null;
  const sourceColumnId = String(cell.columnId || allColumns[index]?.id || "");
  const sourceIndex = Math.max(0, allColumns.findIndex((candidate) => candidate.id === sourceColumnId));
  const sourceSpan = Number.isFinite(Number(cell.colSpan)) ? Math.max(1, Number(cell.colSpan)) : 1;
  const covered = allColumns.slice(sourceIndex, sourceIndex + sourceSpan);
  const visibleCovered = covered.filter((column) => visibleColumns.some((visible) => visible.id === column.id));
  if (!visibleCovered.length) return null;
  const firstVisibleIndex = visibleColumns.findIndex((column) => column.id === visibleCovered[0].id);
  return {
    columnId: visibleCovered[0].id,
    colSpan: visibleCovered.length > 1 ? visibleCovered.length : undefined,
    visibleIndex: firstVisibleIndex,
  };
}

function measureNativeTableRow(cells, columns, padding, style, minHeight = 0) {
  const fontSize = positiveNumber(style.fontSize, 10);
  const lineHeight = positiveNumber(style.lineHeight, 1.25);
  const measured = cells.map((cell, index) => {
    const column = columns.find((candidate) => candidate.id === cell.columnId) || columns[index] || columns[0];
    const span = Math.max(1, Number(cell.colSpan || 1));
    const columnIndex = Math.max(0, columns.findIndex((candidate) => candidate.id === column?.id));
    const width = columns.slice(columnIndex, columnIndex + span).reduce((sum, item) => sum + item.width, 0) || column?.width || 0;
    const border = cell.conditionalStyle ? cell.style.borderWidth ?? 0.5 : 0;
    return estimateTextHeight(String(cell.value ?? ""), Math.max(1, width - padding.left - padding.right - border * 2), { ...style, ...cell.style, fontSize: cell.conditionalStyle ? cell.style.fontSize ?? fontSize : fontSize, lineHeight: cell.conditionalStyle ? cell.style.lineHeight ?? lineHeight : lineHeight }, cell.conditionalStyle)
      + padding.top + padding.bottom + border * 2;
  });
  return { height: Math.max(positiveNumber(minHeight, 0), 1, ...measured.map((value) => positiveNumber(value, 1))) };
}

function buildNativeTableFooter(table, columns, allColumns, rows, padding, style, aggregateContext, defaultScope = "report", payload, visibilityContext, identity) {
  const footer = asRecord(table.footer);
  const broadIdentity = `${identity}/footer`;
  presentationStyle(payload, footer, visibilityContext, broadIdentity, style);
  const cells = Array.isArray(footer.cells) ? footer.cells.map((cellValue, index) => {
    const cell = asRecord(cellValue);
    const column = allColumns.find((candidate) => candidate.id === String(cell.columnId || "")) || columns[index];
    const aggregate = normalizeAggregateSpec(cell.aggregate, column, defaultScope);
    const rawValue = aggregate ? evaluateAggregate(aggregate, aggregateContext) : cell.value;
    const visible = !nativeVisibility(payload) || nativeVisibility(payload).visible(cell, { ...visibilityContext, cell: { value: rawValue } }, `${identity}/footer/cell/${cell.id || index}`);
    const projected = projectTableCellToVisibleColumns(cell, index, allColumns, columns);
    if (!projected) return null;
    const columnId = projected.columnId;
    return {
      columnId,
      colSpan: projected.colSpan,
      value: visible ? formatNativeValue(rawValue, cell.format || aggregate?.format || column?.format) : "",
      ...(visible ? {} : { reportVisible: false }),
      rawValue,
      format: cell.format || aggregate?.format || column?.format,
      ...presentationStyle(payload, cell, { ...visibilityContext, cell: { value: rawValue } }, `${identity}/footer/cell/${cell.id || index}`,
        { ...style, ...asRecord(cell.style), horizontalAlign: cell.horizontalAlign || (nativeVisibility(payload)?.formatting ? cell.style?.horizontalAlign : undefined) || column?.horizontalAlign || style.horizontalAlign || (nativeVisibility(payload)?.formatting && ["money", "number"].includes(cell.format || aggregate?.format || column?.format) ? "right" : undefined) }, broadIdentity),
    };
  }).filter(Boolean) : [];
  if (!cells.length) return null;
  return {
    cells,
    ...measureNativeTableRow(cells, columns, padding, style, positiveNumber(footer.minHeight, 0)),
  };
}

function resolveNativeTableFragment(tablePlan, context) {
  const { table, parentAbsolute, tableAbsolute, fragmentIndex, isContinuation, rows } = context;
  const resolvedRows = rows.map((row) => buildResolvedTableRow(row, tablePlan.columns, paddingForTableRow(tablePlan, row), row.y, row.kind));
  return {
    ...table,
    x: tableAbsolute.x - parentAbsolute.x,
    y: tableAbsolute.y - parentAbsolute.y,
    width: tableAbsolute.width,
    height: tableAbsolute.height,
    sourceBounds: { x: table.x, y: table.y, width: table.width, height: table.height },
    absolute: tableAbsolute,
    overflow: false,
    fragmentIndex,
    isContinuation,
    table: {
      dataset: tablePlan.dataset,
      columns: tablePlan.columns,
      header: null,
      rows: resolvedRows,
      footer: null,
      rowCount: tablePlan.rows.length,
    },
  };
}

function paddingForTableRow(tablePlan, row) {
  if (row.padding) return row.padding;
  if (row.kind === "tableHeader") return tablePlan.headerPadding;
  if (row.kind === "tableFooter") return tablePlan.footerPadding;
  return tablePlan.rowPadding;
}

function buildResolvedTableRow(row, columns, padding, y, kind) {
  let cursorX = 0;
  const cells = row.cells.map((cell, index) => {
    const column = columns.find((candidate) => candidate.id === cell.columnId) || columns[index] || columns[0];
    const span = Math.max(1, Number(cell.colSpan || 1));
    const columnIndex = Math.max(0, columns.findIndex((candidate) => candidate.id === column?.id));
    const spannedColumns = columns.slice(columnIndex, columnIndex + span);
    const width = spannedColumns.length ? spannedColumns.reduce((sum, item) => sum + item.width, 0) : column.width;
    const x = column?.x ?? cursorX;
    cursorX = x + width;
    return {
      ...cell,
      x,
      y,
      width,
      height: row.height,
      padding,
      textX: x + padding.left,
      textY: y + padding.top,
      textWidth: Math.max(0, width - padding.left - padding.right),
      textHeight: Math.max(0, row.height - padding.top - padding.bottom),
    };
  });
  return { kind, rowIndex: row.rowIndex, groupId: row.groupId, groupKey: row.groupKey, sourceId: row.sourceId, repeated: row.repeated, spacer: row.spacer, y, height: row.height, cells };
}

function normalizePage(page) {
  const size = page?.size && PAGE_SIZES[page.size] ? page.size : "Letter";
  const orientation = page?.orientation === "landscape" ? "landscape" : "portrait";
  const base = PAGE_SIZES[size];
  const width = orientation === "landscape" ? base.height : base.width;
  const height = orientation === "landscape" ? base.width : base.height;
  const margins = { ...DEFAULT_MARGINS, ...asRecord(page?.margins) };
  return {
    size,
    orientation,
    width,
    height,
    margins,
    printableArea: {
      x: margins.left,
      y: margins.top,
      width: Math.max(0, width - margins.left - margins.right),
      height: Math.max(0, height - margins.top - margins.bottom),
    },
  };
}

function normalizeBand(value) {
  return {
    id: String(value.id || value.role || "band"),
    role: normalizeRole(value.role),
    height: positiveNumber(value.height, 36),
    sizing: value.sizing === "flow" || value.flow === true ? "flow" : "fixed",
    dataset: typeof value.dataset === "string" ? value.dataset : undefined,
    pageBreakBefore: value.pageBreakBefore === true,
    pageBreakAfter: value.pageBreakAfter === true,
    elements: Array.isArray(value.elements) ? value.elements.map(normalizeElement) : [],
    ...(value.conditions !== undefined ? { conditions: value.conditions } : {}),
    ...(value.visible !== undefined ? { visible: value.visible } : {}),
  };
}

function normalizeElement(value) {
  const type = value.type === "field" ? "field" : value.type === "container" ? "container" : value.type === "table" ? "table" : value.type === "image" ? "image" : "text";
  return {
    id: String(value.id || "element"),
    ...(value.conditions !== undefined ? { conditions: value.conditions } : {}),
    ...(value.visible !== undefined ? { visible: value.visible } : {}),
    type,
    x: positiveNumber(value.x, 0),
    y: positiveNumber(value.y, 0),
    width: positiveNumber(value.width, 72),
    height: positiveNumber(value.height, 14),
    text: typeof value.text === "string" ? value.text : undefined,
    ...(value.expr?.typed !== undefined ? { expr: value.expr } : {}),
    binding: typeof value.binding === "string" ? value.binding : undefined,
    asset: type === "image" && typeof value.asset === "string" ? value.asset : undefined,
    fit: type === "image" && typeof value.fit === "string" ? value.fit : undefined,
    lockAspectRatio: type === "image" && typeof value.lockAspectRatio === "boolean" ? value.lockAspectRatio : undefined,
    dataset: type === "table" && typeof value.dataset === "string" ? value.dataset : undefined,
    format: typeof value.format === "string" ? value.format : undefined,
    style: asRecord(value.style),
    padding: type === "container" ? normalizeSpacing(value.padding) : undefined,
    gap: type === "container" ? positiveNumber(value.gap, 0) : undefined,
    layout: type === "container" ? normalizeContainerLayout(value.layout) : undefined,
    overflow: type === "container" && value.overflow === "visible" ? "visible" : undefined,
    elements: type === "container" && Array.isArray(value.elements) ? value.elements.map(normalizeElement) : undefined,
    columns: type === "table" && Array.isArray(value.columns) ? value.columns.map(asRecord) : undefined,
    header: type === "table" ? asRecord(value.header) : undefined,
    row: type === "table" ? asRecord(value.row) : undefined,
    footer: type === "table" ? asRecord(value.footer) : undefined,
    groups: type === "table" && Array.isArray(value.groups) ? value.groups.map(asRecord) : undefined,
    sort: type === "table" && Array.isArray(value.sort) ? value.sort.map(asRecord) : undefined,
    groupHeader: type === "table" ? asRecord(value.groupHeader) : undefined,
    groupFooter: type === "table" ? asRecord(value.groupFooter) : undefined,
    pagination: type === "table" ? asRecord(value.pagination) : undefined,
    cellPadding: type === "table" ? value.cellPadding : undefined,
    emptyText: type === "table" && typeof value.emptyText === "string" ? value.emptyText : undefined,
  };
}

function isFlowBand(band) {
  return band.sizing === "flow";
}

function validateFlowBandShape(band) {
  const tables = band.elements.filter((element) => element.type === "table");
  if (tables.length !== 1) return `Flow band "${band.id}" must contain exactly one native table.`;
  const table = tables[0];
  const tableTop = table.y;
  const overlapping = band.elements.filter((element) => element.type !== "table" && element.y + element.height > tableTop);
  if (overlapping.length) {
    return `Flow band "${band.id}" only supports positioned prefix elements that end before table "${table.id}" starts.`;
  }
  return "";
}

function normalizeContainerLayout(layout) {
  const record = asRecord(layout);
  const mode = record.mode === "vertical" || record.mode === "columns" ? record.mode : "absolute";
  return {
    mode,
    gap: positiveNumber(record.gap, 0),
    columns: Array.isArray(record.columns) ? record.columns : [],
    horizontalAlign: ["left", "center", "right", "stretch"].includes(record.horizontalAlign) ? record.horizontalAlign : "stretch",
    verticalAlign: ["top", "middle", "bottom", "stretch"].includes(record.verticalAlign) ? record.verticalAlign : "top",
  };
}

function normalizeSpacing(value) {
  if (typeof value === "number") {
    const size = Math.max(0, value);
    return { top: size, right: size, bottom: size, left: size };
  }
  const record = asRecord(value);
  const fallback = positiveNumber(record.all, 0);
  return {
    top: positiveNumber(record.top, fallback),
    right: positiveNumber(record.right, fallback),
    bottom: positiveNumber(record.bottom, fallback),
    left: positiveNumber(record.left, fallback),
  };
}

function elementExtendsBeyond(child, parentContent) {
  const tolerance = 0.01;
  return child.x < parentContent.x - tolerance
    || child.y < parentContent.y - tolerance
    || child.x + child.width > parentContent.x + parentContent.width + tolerance
    || child.y + child.height > parentContent.y + parentContent.height + tolerance;
}

function groupBandsByRole(bands) {
  const grouped = {
    reportHeader: [],
    pageHeader: [],
    detail: [],
    reportFooter: [],
    pageFooter: [],
    groupHeader: [],
    groupFooter: [],
  };
  bands.forEach((band) => grouped[band.role].push(band));
  return grouped;
}

function createPage(pageSetup, pageNumber) {
  return {
    pageNumber,
    width: pageSetup.width,
    height: pageSetup.height,
    printableArea: pageSetup.printableArea,
    bands: [],
  };
}

function sumBandHeights(bands) {
  return bands.reduce((sum, band) => sum + band.height, 0);
}

function detailRows(payload, datasetName) {
  const dataset = getDataset(payload, datasetName);
  return rowsFromDataset(dataset);
}

function tableRowsInfo(payload, datasetName, scopedRow) {
  if (!datasetName && !scopedRow) return { rows: [], reason: "missingDataset" };
  const dataset = datasetName ? getDataset(payload, datasetName) : scopedRow;
  if (dataset === undefined || dataset === null) {
    return { rows: [], reason: datasetName && nativeDatasetMetadataExists(payload, datasetName) ? "noSampleData" : "invalidDataset" };
  }
  if (Array.isArray(dataset)) return { rows: dataset, reason: dataset.length ? "ok" : "zeroRecords" };
  const record = asRecord(dataset);
  for (const key of ["rows", "Rows", "items", "Items", "entries", "Entries", "details", "Details"]) {
    if (Array.isArray(record[key])) return { rows: record[key], reason: record[key].length ? "ok" : "zeroRecords" };
  }
  if (!Object.keys(record).length && datasetName && nativeDatasetMetadataExists(payload, datasetName)) return { rows: [], reason: "noSampleData" };
  return { rows: [], reason: "notCollection" };
}

function rowsFromDataset(dataset) {
  if (Array.isArray(dataset)) return dataset;
  const record = asRecord(dataset);
  for (const key of ["rows", "Rows", "items", "Items", "entries", "Entries", "details", "Details"]) {
    if (Array.isArray(record[key])) return record[key];
  }
  return [];
}

function nativeTableEmptyText(datasetName, rowsInfo) {
  if (!datasetName) return "Select a repeating dataset.";
  if (rowsInfo.reason === "invalidDataset") return `Dataset "${datasetName}" is not available.`;
  if (rowsInfo.reason === "notCollection") return `Dataset "${datasetName}" is not a repeating collection.`;
  if (rowsInfo.reason === "noSampleData") return `No sample data loaded for "${datasetName}".`;
  if (rowsInfo.reason === "zeroRecords") return `Dataset "${datasetName}" returned zero records.`;
  return "No records.";
}

function nativeDatasetMetadataExists(payload, datasetName) {
  const datasets = payload?.data?.datasets;
  if (!Array.isArray(datasets)) return false;
  return datasets.some((value) => {
    const record = asRecord(value);
    return [record.id, record.key, record.name, record.source].map((item) => String(item || "")).includes(datasetName);
  });
}

function resolveTemplate(payload, template, row, datasetName) {
  return template.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_, path) => formatNativeValue(resolveNativeBinding(payload, String(path), row, datasetName)));
}

// PDFKit's standard Helvetica advances, in thousandths of an em, for ASCII 32-126.
// Oblique variants share advances; a 2% wrap reserve covers rounding/Arial substitution.
// Other glyphs retain a conservative bound.
const HELVETICA_ADVANCES = [
  [278,278,355,556,556,889,667,191,333,333,389,584,278,333,278,278,556,556,556,556,556,556,556,556,556,556,278,278,584,584,584,556,1015,667,667,722,722,667,611,778,722,278,500,667,556,833,722,778,667,778,722,667,611,722,667,944,667,667,611,278,278,278,469,556,333,556,556,500,556,556,278,556,556,222,222,500,222,833,556,556,556,556,333,500,278,556,500,722,500,500,500,334,260,334,584],
  [278,333,474,556,556,889,722,238,333,333,389,584,278,333,278,278,556,556,556,556,556,556,556,556,556,556,333,333,584,584,584,611,975,722,722,722,722,667,611,778,722,278,556,722,611,833,722,778,667,778,722,667,611,722,667,944,667,667,611,333,278,333,584,556,333,556,611,556,611,556,333,611,611,278,278,556,278,889,611,611,611,611,389,556,333,611,556,778,556,556,500,389,280,389,584],
];

function estimateTextHeight(value, width, style, resolvedStyle = false) {
  const fontSize = positiveNumber(style?.fontSize, 10);
  const lineHeight = positiveNumber(style?.lineHeight, 1.2);
  const charactersPerLine = Math.max(1, Math.floor(width / Math.max(4, fontSize * 0.52)));
  const advances = HELVETICA_ADVANCES[style?.bold ? 1 : 0];
  const capacity = Math.max(1, width) / fontSize;
  const lines = String(value || "").split(/\r?\n/).reduce((sum, line) => {
    if (!resolvedStyle) return sum + Math.max(1, Math.ceil(line.length / charactersPerLine));
    let count = 1;
    let used = 0;
    for (const token of line.split(/(\s+)/).filter(Boolean)) {
      const widths = Array.from(token, (character) => (advances[character.charCodeAt(0) - 32] ?? 1100) * 1.02 / 1000);
      const tokenWidth = widths.reduce((total, advance) => total + advance, 0);
      if (!token.trim()) { if (used) used += tokenWidth; continue; }
      if (used && used + tokenWidth > capacity) { count++; used = 0; }
      for (const advance of widths) {
        if (used && used + advance > capacity) { count++; used = 0; }
        used += advance;
      }
    }
    return sum + count;
  }, 0);
  return lines * fontSize * lineHeight;
}

function getDataset(payload, name) {
  if (!name) return payload.datasets;
  return name.split(".").reduce((current, key) => asRecord(current)[key], payload.datasets);
}

function getPath(value, path) {
  if (!path) return undefined;
  return path.split(".").reduce((current, key) => asRecord(current)[key], value);
}

function hasPath(record, path) {
  return getPath(record, path) !== undefined;
}

function normalizeRole(value) {
  if (value === "pageHeader" || value === "detail" || value === "reportFooter" || value === "pageFooter" || value === "groupHeader" || value === "groupFooter") return value;
  return "reportHeader";
}

function positiveNumber(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : fallback;
}

function numberOrDefault(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function asRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}
